import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import { showErrorToast, showSuccessToast, ConfirmDialog } from '@/components';
import {
  getShipmentDetail,
  cancelShipment,
  isShipmentCancellable,
  isFailedAwaitingReturn,
  itemLegs,
  Direct4WEvidence,
  Direct4WSideEvidence,
  ShipmentDetail,
  ShipmentItem,
  ShipmentRouteMilestone,
  ShipmentTimelineAttachment,
  ShipmentTimelineEvent,
  type WorkflowSubmittedVia,
} from '@/services/api/shipments';
import { cancelPackage } from '@/services/api/packages';
import { getBackendErrorMessage } from '@/utils/errorHandler';
import EditPackageModal from '@/pages/packages/EditPackageModal';
import {
  ConditionBadge,
  PackageStatusBadge,
} from '@/pages/packages/PackageStatusBadge';
import {
  getShipmentStatusLabel,
  ShipmentStatusBadge,
} from './ShipmentStatusBadge';
import ShipmentEximBadge from './ShipmentEximBadge';
import {
  documentLabel,
  EXPORT_DOCS,
  IMPORT_DOCS,
  isPatimbanClient,
  storedDocuments,
} from './officialDocuments';
import ItemPodGallery from './ItemPodGallery';
import PhotoTileGrid from './PhotoTileGrid';

// Lazy: @react-pdf/renderer is a large dependency and most drawer opens never
// print, so it is fetched on the first "Cetak foto" click instead of riding
// along in the main bundle.
const EvidencePrintModal = React.lazy(() => import('./EvidencePrintModal'));

// Items are only cancellable while still CREATED, and editable while CREATED or
// HUB_SCANNED — matches the packages menu (CANCELLABLE_STATUSES /
// EDITABLE_STATUSES) and the backend, which rejects anything further along.
const CANCELLABLE_ITEM_STATUS = 'CREATED';
const EDITABLE_ITEM_STATUSES = ['CREATED', 'HUB_SCANNED'];

const fmt = (v?: string | null) =>
  v ? dayjs(v).format('DD MMM YYYY, HH:mm') : '—';

const dash = (v?: string | number | null) =>
  v === null || v === undefined || v === '' ? '—' : v;

// Humanize an upper-snake enum, e.g. INSTANT → "Instant", SAME_DAY → "Same Day".
const humanize = (s?: string | null) =>
  s
    ? s
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : '';

// Build a Google Maps link from coordinates (preferred) or a free-text address.
const mapsHref = (
  address?: string | null,
  lat?: number | null,
  long?: number | null
) => {
  const q =
    lat != null && long != null ? `${lat},${long}` : address || undefined;
  return q
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`
    : undefined;
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="border-b border-stroke-soft-200 px-6 py-5 last:border-b-0">
    <h3 className="mb-3.5 text-[11px] font-bold uppercase tracking-[0.08em] text-text-soft-400">
      {title}
    </h3>
    {children}
  </div>
);

const KV: React.FC<{ k: string; children: React.ReactNode }> = ({
  k,
  children,
}) => (
  <div className="flex items-start justify-between gap-4 py-1.5 text-[13px]">
    <span className="text-text-sub-600">{k}</span>
    <span className="text-right font-semibold text-text-strong-950">
      {children}
    </span>
  </div>
);

const STOP_TYPE_LABELS: Record<string, string> = {
  PICKUP: 'Pickup',
  DROP_OFF: 'Drop-off',
};

// Route-stop status → label + text tone. Unknown statuses fall back to the
// humanized raw value in the neutral tone.
const STOP_STATUS: Record<string, { label: string; tone: string }> = {
  PENDING: { label: 'Pending', tone: 'text-text-soft-400' },
  COMPLETED: { label: 'Selesai', tone: 'text-success-base' },
  FAILED: { label: 'Gagal', tone: 'text-error-base' },
  CANCELLED: { label: 'Dibatalkan', tone: 'text-error-base' },
  SKIPPED: { label: 'Dilewati', tone: 'text-text-soft-400' },
};

const RouteNode: React.FC<{
  /** origin = hollow primary dot, destination = filled green, stop = grey. */
  kind: 'origin' | 'destination' | 'stop';
  /** Overrides the default "Origin" / "Destination" caption. */
  label?: string;
  /** Route-stop status (4W stop list only). */
  status?: string | null;
  name?: string | null;
  address?: string | null;
  lat?: number | null;
  long?: number | null;
  phone?: string | null;
  showConnector?: boolean;
}> = ({
  kind,
  label,
  status,
  name,
  address,
  lat,
  long,
  phone,
  showConnector,
}) => {
  const href = mapsHref(address, lat, long);
  const statusMeta = status
    ? (STOP_STATUS[status] ?? {
        label: humanize(status),
        tone: 'text-text-soft-400',
      })
    : null;
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center self-stretch">
        <span
          className={`mt-0.5 h-[18px] w-[18px] shrink-0 rounded-full border-2 ${
            kind === 'origin'
              ? 'border-primary bg-bg-white-0'
              : kind === 'destination'
                ? 'border-success-base bg-success-base'
                : 'border-stroke-sub-300 bg-bg-white-0'
          }`}
        />
        {showConnector && (
          <span className="my-1 w-0.5 flex-1 bg-stroke-soft-200" />
        )}
      </div>
      <div className={`min-w-0 flex-1 ${showConnector ? 'pb-5' : ''}`}>
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.08em] text-text-soft-400">
          <span>{label ?? (kind === 'origin' ? 'Origin' : 'Destination')}</span>
          {statusMeta && (
            <span className={statusMeta.tone}>· {statusMeta.label}</span>
          )}
        </div>
        {name && (
          <div className="mt-0.5 font-semibold text-text-strong-950">
            {name}
          </div>
        )}
        {address && (
          <div className="mt-0.5 text-[13px] leading-snug text-text-strong-950">
            {address}
          </div>
        )}
        {lat != null && long != null && (
          <div className="mt-1 font-mono text-[11.5px] text-text-soft-400">
            {lat}, {long}
          </div>
        )}
        {phone && (
          <div className="mt-0.5 font-mono text-xs text-text-sub-600">
            {phone}
          </div>
        )}
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-stroke-sub-300 bg-bg-white-0 px-2.5 py-1 text-xs font-medium text-text-sub-600 hover:bg-bg-weak-50"
          >
            <i className="ri-map-pin-line" />
            Open in Maps
          </a>
        )}
      </div>
    </div>
  );
};

// ─── Signature fields ─────────────────────────────────────────────────
// A SIGNATURE stop-workflow field, wherever the drawer shows one (timeline
// attachment or 4W start/end evidence):
//
//   {label} · {time}
//   [ 112×56 tile, uncropped on white ]  [Diisi ops · foto surat jalan]
//
// No "Diterima oleh" line: the recipient's name, when the workflow asks for
// it, is its own TEXT field and renders as an ordinary row. The badge marks a
// milestone ops filled on the rider's behalf — there the file is a photo of the
// signed delivery note, not a signature drawn on the phone. `submittedVia`
// missing (older gateway) reads as no badge.

const SIGNATURE_FALLBACK_LABEL = 'Tanda tangan';
const OPS_SOURCE_BADGE_LABEL = 'Diisi ops · foto surat jalan';

const SignatureField: React.FC<{
  label?: string | null;
  urls: string[];
  /** When the milestone was submitted; shown beside the label. */
  submittedAt?: string | null;
  submittedVia?: WorkflowSubmittedVia | null;
  labelClassName?: string;
}> = ({
  label,
  urls,
  submittedAt,
  submittedVia,
  labelClassName = 'text-[11px] font-semibold text-text-sub-600',
}) => {
  const name = label?.trim() || SIGNATURE_FALLBACK_LABEL;
  const hasSignature = urls.length > 0;
  return (
    <div>
      {/* Time only once something was signed — an optional field left empty
          has no signing moment to show. */}
      <div className={labelClassName}>
        {hasSignature && submittedAt ? `${name} · ${fmt(submittedAt)}` : name}
      </div>
      {hasSignature ? (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <PhotoTileGrid urls={urls} alt={name} variant="signature" />
          {submittedVia === 'OPS' && (
            <span className="rounded bg-warning-lighter px-1.5 py-0.5 text-[10.5px] font-bold text-warning-dark">
              {OPS_SOURCE_BADGE_LABEL}
            </span>
          )}
        </div>
      ) : (
        <PhotoTileGrid
          urls={[]}
          alt={name}
          emptyLabel="Belum ada tanda tangan"
        />
      )}
    </div>
  );
};

// ─── Timeline ─────────────────────────────────────────────────────────
// Status transitions and driver milestones in one stream, newest first. Same
// rail construction as RouteNode: a flex column holding the dot and a
// flex-1 connector, so the line is centred under the dot by layout rather than
// by an offset that has to be kept in sync.

const TIMELINE_ICON: Record<string, string> = {
  STATUS_CHANGE: 'ri-swap-line',
  WORKFLOW_MILESTONE: 'ri-camera-line',
  NOTE: 'ri-sticky-note-line',
};

// One captured field. IMAGE becomes photo tiles (ImagePreview gives us the
// lightbox), SIGNATURE a signature tile; TEXT/NUMBER render as a label/value
// row. `value` is always an array from the gateway, so scalars just join.
//
// `type` is a plain string, so a new file type has to be branched on here by
// hand — the compiler won't flag it, and anything unlisted falls through to
// the text row (which is how a SIGNATURE used to show up as a bare URL).
const TimelineAttachment: React.FC<{
  attachment: ShipmentTimelineAttachment;
  /** The owning event's time and source — a signature row shows both. */
  occurredAt: string;
  submittedVia?: WorkflowSubmittedVia | null;
}> = ({ attachment, occurredAt, submittedVia }) => {
  if (attachment.type === 'SIGNATURE') {
    return (
      <SignatureField
        label={attachment.name}
        urls={attachment.value.map(String).filter(Boolean)}
        submittedAt={occurredAt}
        submittedVia={submittedVia}
      />
    );
  }
  if (attachment.type === 'IMAGE') {
    const urls = attachment.value.map(String).filter(Boolean);
    return (
      <div>
        <div className="text-[11px] font-semibold text-text-sub-600">
          {attachment.name}
        </div>
        <PhotoTileGrid
          urls={urls}
          alt={attachment.name}
          emptyLabel="Belum ada foto"
          className="mt-1"
        />
      </div>
    );
  }
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-[11px] text-text-sub-600">{attachment.name}</span>
      <span className="text-right text-[12px] font-semibold text-text-strong-950">
        {attachment.value.length > 0 ? attachment.value.join(', ') : '—'}
      </span>
    </div>
  );
};

const TimelineEvent: React.FC<{
  event: ShipmentTimelineEvent;
  showConnector: boolean;
}> = ({ event, showConnector }) => {
  const isStatusChange = event.eventType === 'STATUS_CHANGE';
  return (
    <li className="flex gap-3">
      <div className="flex flex-col items-center self-stretch">
        <span className="mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border border-stroke-soft-200 bg-bg-white-0">
          <i
            className={`${
              TIMELINE_ICON[event.eventType] ?? 'ri-circle-line'
            } text-[10px] text-text-sub-600`}
          />
        </span>
        {showConnector && (
          <span className="my-1 w-0.5 flex-1 bg-stroke-soft-200" />
        )}
      </div>

      <div className={`min-w-0 flex-1 ${showConnector ? 'pb-5' : ''}`}>
        <div className="flex items-start justify-between gap-3">
          {isStatusChange ? (
            <span className="flex flex-wrap items-center gap-1.5">
              <ShipmentStatusBadge status={event.status} />
              {event.fromStatus && (
                <span className="text-[11px] text-text-soft-400">
                  dari {getShipmentStatusLabel(event.fromStatus)}
                </span>
              )}
            </span>
          ) : (
            <span className="font-semibold text-text-strong-950">
              {event.title || humanize(event.milestoneKey) || 'Event'}
            </span>
          )}
          <span className="shrink-0 font-mono text-[11px] text-text-soft-400">
            {fmt(event.occurredAt)}
          </span>
        </div>

        {/* A milestone does not move the status, so name where the shipment
            stood — otherwise the reader scans up for the last transition. */}
        {!isStatusChange && (
          <div className="mt-0.5 text-[11px] text-text-soft-400">
            {getShipmentStatusLabel(event.status)}
          </div>
        )}
        {event.note && (
          <div className="mt-1 text-[12px] leading-snug text-text-sub-600">
            {event.note}
          </div>
        )}

        {event.attachments.length > 0 && (
          <div className="mt-2 space-y-2 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2.5">
            {event.attachments.map((a) => (
              <TimelineAttachment
                key={a.key}
                attachment={a}
                occurredAt={event.occurredAt}
                submittedVia={event.submittedVia}
              />
            ))}
          </div>
        )}
      </div>
    </li>
  );
};

// Retur details for one item (Status Model v2 §4) — the reason code/note and the
// weight recorded when it went back. These used to be write-only on the BE;
// renders nothing until a retur actually happened.
const ReturPanel: React.FC<{ item: ShipmentItem }> = ({ item }) => {
  if (
    !item.returReasonCode &&
    !item.returReasonNote &&
    item.returWeight == null
  )
    return null;

  return (
    <div className="mt-2.5 rounded-md border border-warning-lighter bg-warning-lighter/40 px-2.5 py-2">
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-warning-dark">
        <i className="ri-corner-up-left-line text-[13px]" />
        Retur
      </div>
      <div className="mt-1 space-y-0.5 text-[12.5px] text-text-sub-600">
        {item.returReasonCode && (
          <div>
            Alasan{' '}
            <span className="font-mono font-semibold text-text-strong-950">
              {item.returReasonCode}
            </span>
          </div>
        )}
        {item.returReasonNote && <div>{item.returReasonNote}</div>}
        {item.returWeight != null && (
          <div>
            Berat retur{' '}
            <span className="font-mono font-semibold text-text-strong-950">
              {item.returWeight} kg
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Tone for a leg's delivery-service status (DeliveryStatus is unchanged by v2).
const legStatusClass = (status?: string | null) => {
  if (status === 'COMPLETED') return 'text-success-dark';
  if (status === 'FAILED') return 'text-error-dark';
  return 'text-text-sub-600';
};

// Delivery legs (Status Model v2 §4) — one row per forward attempt plus the trip
// back to the hub, replacing the old single `externalDeliveryUid` /
// `de2DeliveryUid` pair. Two attempts on an item is normal, not an anomaly.
const LegList: React.FC<{ item: ShipmentItem }> = ({ item }) => {
  const legs = itemLegs(item);
  if (legs.length === 0) return null;

  let attempt = 0;
  return (
    <div className="mt-2.5 rounded-md border border-stroke-soft-200 px-2.5 py-2">
      <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-text-soft-400">
        Delivery legs
      </div>
      <div className="mt-1 space-y-1">
        {legs.map((leg, i) => {
          const isReturn = leg.legType === 'RETURN_TO_HUB';
          if (!isReturn) attempt += 1;
          return (
            <div
              key={leg.id || `${leg.legSeq ?? i}`}
              className="flex items-center justify-between gap-3 text-[12.5px]"
            >
              <span className="text-text-sub-600">
                {isReturn ? 'Retur ke hub' : `Antar #${attempt}`}
                {leg.deliveryUid && (
                  <>
                    {' · '}
                    <span className="font-mono text-text-strong-950">
                      {leg.deliveryUid}
                    </span>
                  </>
                )}
                {leg.isActive && (
                  <span className="ml-1.5 rounded bg-bg-soft-200 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-text-sub-600">
                    Active
                  </span>
                )}
              </span>
              <span
                className={`shrink-0 font-semibold ${legStatusClass(leg.deliveryStatus)}`}
              >
                {humanize(leg.deliveryStatus) || '—'}
              </span>
            </div>
          );
        })}
      </div>
      {/* Failed leg while the item is still IN_DELIVERY = physically with the
          rider, no retur leg opened yet. */}
      {isFailedAwaitingReturn(item) && (
        <div className="mt-1.5 flex items-start gap-1.5 text-[12px] leading-snug text-warning-dark">
          <i className="ri-error-warning-line mt-px shrink-0 text-[13px]" />
          Gagal antar, barang belum kembali ke hub.
        </div>
      )}
    </div>
  );
};

// ─── 4W driver evidence ───────────────────────────────────────────────
// DIRECT_4W reads as a point-to-point trip, so the gateway hands us its two
// ends as a pair (`direct4wEvidence`). Each end shows its milestone photos and
// the vehicle-condition check; the "Cetak foto" button assembles the whole route
// into the printable Dokumen Lampiran (see EvidencePrintModal).

// A milestone's name, submit time and photo/signature tiles — shared by the
// start/end evidence cards.
const MilestonePhotos: React.FC<{ milestones: ShipmentRouteMilestone[] }> = ({
  milestones,
}) => (
  <>
    {milestones.map((m) => (
      <div key={m.key}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[12px] font-semibold text-text-strong-950">
            {m.name}
          </span>
          {m.submittedAt && (
            <span className="font-mono text-[11px] text-text-soft-400">
              {fmt(m.submittedAt)}
            </span>
          )}
        </div>
        {m.photoFields.map((f) =>
          f.type === 'SIGNATURE' ? (
            // Always labelled: a lone signature tile under the milestone name
            // would not say whose mark it is.
            <div key={f.fieldKey} className="mt-1">
              <SignatureField
                label={f.label}
                urls={f.urls}
                submittedAt={m.submittedAt}
                submittedVia={m.submittedVia}
                labelClassName="text-[11px] text-text-sub-600"
              />
            </div>
          ) : (
            <div key={f.fieldKey} className="mt-1">
              {m.photoFields.length > 1 && (
                <div className="text-[11px] text-text-sub-600">{f.label}</div>
              )}
              <PhotoTileGrid
                urls={f.urls}
                alt={f.label}
                emptyLabel="Belum ada foto"
                className="mt-1"
              />
            </div>
          )
        )}
      </div>
    ))}
  </>
);

// One end of the trip. Every stop field can be null (the gateway surfaces a
// vehicle check even before its stop resolves), so the header degrades to the
// bare label rather than rendering "null".
const Direct4WSideCard: React.FC<{
  label: string;
  side: Direct4WSideEvidence;
}> = ({ label, side }) => {
  const stopLabel = side.type
    ? (STOP_TYPE_LABELS[side.type] ?? side.type)
    : null;
  const timestamp = side.resolvedAt ?? side.arrivedAt;
  const isEmpty = side.milestones.length === 0 && !side.vehicleCheck;
  return (
    <div className="rounded-lg border border-stroke-soft-200 p-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="text-[12px] font-bold tracking-tight text-text-strong-950">
            {label}
            {stopLabel ? ` · ${stopLabel}` : ''}
          </span>
          {side.address && (
            <div className="mt-0.5 text-[11px] leading-snug text-text-sub-600">
              {side.address}
            </div>
          )}
          {timestamp && (
            <div className="mt-0.5 font-mono text-[11px] text-text-soft-400">
              {fmt(timestamp)}
            </div>
          )}
        </div>
        {side.status && (
          <span className="shrink-0 rounded-md bg-bg-soft-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-text-sub-600">
            {side.status}
          </span>
        )}
      </div>

      {isEmpty ? (
        <div className="mt-2.5 border-t border-stroke-soft-200 pt-2.5 text-[11px] italic text-text-soft-400">
          Belum ada evidence
        </div>
      ) : (
        <div className="mt-2.5 space-y-2.5 border-t border-stroke-soft-200 pt-2.5">
          <MilestonePhotos milestones={side.milestones} />

          {side.vehicleCheck && (
            <div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[12px] font-semibold text-text-strong-950">
                  Kondisi kendaraan
                </span>
                {side.vehicleCheck.submittedAt && (
                  <span className="font-mono text-[11px] text-text-soft-400">
                    {fmt(side.vehicleCheck.submittedAt)}
                  </span>
                )}
              </div>
              <div className="mt-0.5 text-[11px] text-text-sub-600">
                Odometer:{' '}
                <span className="font-mono font-semibold text-text-strong-950">
                  {side.vehicleCheck.odometerReading.toLocaleString('id-ID')} km
                </span>
              </div>
              <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1.5 sm:grid-cols-3">
                {side.vehicleCheck.photoFields.map((f) => (
                  <div key={f.fieldKey}>
                    <div className="text-[11px] text-text-sub-600">
                      {f.label}
                    </div>
                    <PhotoTileGrid
                      urls={f.urls}
                      alt={f.label}
                      emptyLabel="Belum ada foto"
                      className="mt-1"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// The start/end evidence pair plus the "Cetak foto" action. Renders nothing when
// neither end has captured anything.
const Direct4WEvidenceSection: React.FC<{
  evidence: Direct4WEvidence;
  onPrint: () => void;
}> = ({ evidence, onPrint }) => {
  if (!evidence.start && !evidence.end) return null;
  return (
    <div className="border-b border-stroke-soft-200 px-6 py-5 last:border-b-0">
      <div className="mb-3.5 flex items-center justify-between gap-2">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.08em] text-text-soft-400">
          Start &amp; end evidence
        </h3>
        <button
          type="button"
          onClick={onPrint}
          className="inline-flex items-center gap-1.5 rounded-md border border-stroke-sub-300 bg-bg-white-0 px-2.5 py-1 text-xs font-medium text-text-sub-600 transition-colors hover:bg-bg-weak-50"
        >
          <i className="ri-printer-line" />
          Cetak foto
        </button>
      </div>
      <p className="-mt-1.5 mb-3 text-[11px] text-text-soft-400">
        4W trip{' '}
        <span className="font-mono text-text-sub-600">
          {evidence.routeCode}
        </span>{' '}
        · terbaru di atas · foto diambil driver di tiap ujung perjalanan.
      </p>
      {/* Newest first, same as the Timeline: End above Start. */}
      <div className="space-y-2.5">
        {evidence.end && <Direct4WSideCard label="End" side={evidence.end} />}
        {evidence.start && (
          <Direct4WSideCard label="Start" side={evidence.start} />
        )}
      </div>
    </div>
  );
};

interface ShipmentDetailDrawerProps {
  shipmentID: string | null;
  onClose: () => void;
  // Print the loaded shipment's labels (reuses the already-fetched detail, no
  // extra request). Omit to hide the Print action.
  onPrint?: (detail: ShipmentDetail) => void;
  // Fired after a shipment is cancelled here, so the parent list can refresh
  // its rows + summary counts.
  onCancelled?: () => void;
  /** The logged-in company's name, for when the shipment omits its client
   *  snapshot (Link is single-client) — picks Patimban's fixed doc slots. */
  clientName?: string | null;
  /** Who is cancelling (the logged-in user), recorded by the BE as the
   *  cancel's actor — provider-user tokens carry no email of their own. */
  cancelledBy?: string;
}

const ShipmentDetailDrawer: React.FC<ShipmentDetailDrawerProps> = ({
  shipmentID,
  onClose,
  onPrint,
  onCancelled,
  clientName,
  cancelledBy,
}) => {
  const [detail, setDetail] = useState<ShipmentDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<ShipmentItem | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [editTarget, setEditTarget] = useState<ShipmentItem | null>(null);
  // Whole-shipment cancel (distinct from the per-item cancel above).
  const [cancelShipmentOpen, setCancelShipmentOpen] = useState(false);
  const [cancellingShipment, setCancellingShipment] = useState(false);
  // "Cetak foto" — the 4W evidence Dokumen Lampiran print/download modal.
  const [printOpen, setPrintOpen] = useState(false);
  // Every stop on the 4W route, in driving order (the gateway already sorts
  // by sequence; re-sort so the list never depends on it).
  const fourWheelStops = [...(detail?.route?.stops ?? [])].sort(
    (a, b) => a.sequence - b.sequence
  );
  const activeIdRef = useRef<string | null>(null);

  const load = useCallback(async (id: string) => {
    activeIdRef.current = id;
    setLoading(true);
    setDetail(null);
    try {
      const data = await getShipmentDetail(id);
      if (activeIdRef.current !== id) return; // a newer request superseded this
      setDetail(data);
    } catch (error) {
      if (activeIdRef.current !== id) return;
      showErrorToast(
        'Gagal memuat detail',
        error instanceof Error ? error.message : 'Terjadi kesalahan.'
      );
      onClose();
    } finally {
      if (activeIdRef.current === id) setLoading(false);
    }
    // onClose is stable enough; intentionally not a dep to avoid refetch loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shipmentID) load(shipmentID);
  }, [shipmentID, load]);

  // Soft-cancel one item, then reload the detail so the item's status and the
  // shipment totals (which the backend recomputes) reflect the change.
  const handleCancel = async () => {
    if (!cancelTarget?.id) return;
    setCancelling(true);
    try {
      await cancelPackage(cancelTarget.id);
      showSuccessToast(
        'Paket dibatalkan',
        `${cancelTarget.itemName || cancelTarget.invoice || 'Paket'} telah dibatalkan.`
      );
      setCancelTarget(null);
      if (shipmentID) load(shipmentID);
    } catch (err) {
      showErrorToast('Gagal membatalkan', getBackendErrorMessage(err));
    } finally {
      setCancelling(false);
    }
  };

  // Cancel the whole shipment (only while CREATED). Reload the detail so the
  // header + item statuses reflect CANCELLED, and notify the parent list.
  const handleCancelShipment = async () => {
    if (!detail) return;
    setCancellingShipment(true);
    try {
      await cancelShipment(detail.id, { cancelledBy });
      showSuccessToast(
        'Shipment dibatalkan',
        `${detail.waybill} telah dibatalkan.`
      );
      setCancelShipmentOpen(false);
      onCancelled?.();
      if (shipmentID) load(shipmentID);
    } catch (err) {
      showErrorToast('Gagal membatalkan', getBackendErrorMessage(err));
    } finally {
      setCancellingShipment(false);
    }
  };

  if (!shipmentID) return null;

  const items = detail?.items ?? [];
  // Newest first, exactly as the gateway returns it — no client-side re-sort,
  // so the order always matches what BE considers the event order.
  const timeline = detail?.timeline ?? [];
  const canCancelShipment = !!detail && isShipmentCancellable(detail.status);
  // Total koli for the shipment: prefer the BE aggregate, fall back to summing
  // the items in hand (each item defaults to 1 koli — BE NOT NULL DEFAULT 1).
  const totalKoli =
    detail?.totalKoli ??
    (items.length > 0
      ? items.reduce((sum, it) => sum + (it.koli ?? 1), 0)
      : undefined);

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-40 bg-bg-strong-950/40"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="fixed right-0 top-0 z-50 flex h-full w-[640px] max-w-[95vw] flex-col bg-bg-white-0 shadow-card-lg">
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between border-b border-stroke-soft-200 px-6 py-5">
          <div className="min-w-0">
            {detail && (
              <div className="mb-2 flex items-center gap-2">
                <ShipmentStatusBadge status={detail.status} />
                <ShipmentEximBadge exim={detail.exim} />
                {detail.serviceType && (
                  <span className="rounded-md bg-bg-soft-200 px-2 py-0.5 text-[11px] font-bold tracking-wide text-text-sub-600">
                    {humanize(detail.serviceType)}
                  </span>
                )}
              </div>
            )}
            <h2 className="truncate font-mono text-xl font-semibold tracking-tight text-text-strong-950">
              {detail?.waybill ?? shipmentID}
            </h2>
            {detail?.bookingId && (
              <p className="mt-0.5 font-mono text-xs text-text-soft-400">
                Booking ID ·{' '}
                <span className="font-semibold text-text-sub-600">
                  {detail.bookingId}
                </span>
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {canCancelShipment && (
              <button
                type="button"
                onClick={() => setCancelShipmentOpen(true)}
                className="flex items-center gap-1.5 rounded-lg border border-error-base px-3 py-1.5 text-sm font-medium text-error-base transition-colors hover:bg-error-lighter"
              >
                <i className="ri-close-circle-line text-base" />
                Cancel shipment
              </button>
            )}
            {/* Named in full: the evidence section carries its own "Cetak foto"
                export, so a bare "Print" here would be ambiguous. */}
            {onPrint && detail && items.length > 0 && (
              <button
                type="button"
                onClick={() => onPrint(detail)}
                className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-static-white transition-colors hover:opacity-90"
              >
                <i className="ri-printer-line text-base" />
                Print Shipment Label
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-text-soft-400 hover:bg-bg-weak-50 hover:text-text-sub-600"
              aria-label="Close"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
        </div>

        {/* Body */}
        {loading || !detail ? (
          <div className="flex flex-1 items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-2xl text-text-soft-400" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {/* Details */}
            <Section title="Details">
              <KV k="Service">
                {detail.serviceType ? humanize(detail.serviceType) : '—'}
              </KV>
              <KV k="Delivery group">
                <span className="font-mono">
                  {dash(detail.deliveryGroupId)}
                </span>
              </KV>
              {/* Parcel rollups — hidden for DIRECT_4W (always zero on a
                  point-to-point trip); the driver/vehicle snapshot shows
                  instead, mirroring the ops Details section. */}
              {detail.type !== 'DIRECT_4W' && (
                <>
                  <KV k="Items">{dash(detail.itemCount ?? items.length)}</KV>
                  <KV k="Total koli">{dash(totalKoli)}</KV>
                  <KV k="Total weight">
                    {detail.totalWeight != null ? (
                      <span className="font-mono">{detail.totalWeight} kg</span>
                    ) : (
                      '—'
                    )}
                  </KV>
                  <KV k="Total volume">
                    {detail.totalVolume != null ? (
                      <span className="font-mono">{detail.totalVolume} m³</span>
                    ) : (
                      '—'
                    )}
                  </KV>
                </>
              )}
              {detail.type === 'DIRECT_4W' && (
                <>
                  <KV k="Driver code">
                    <span className="font-mono">
                      {dash(detail.route?.driverCode)}
                    </span>
                  </KV>
                  <KV k="Driver name">{dash(detail.route?.driverName)}</KV>
                  <KV k="Plat nomor">
                    <span className="font-mono">
                      {dash(detail.route?.vehicle?.plateNumber)}
                    </span>
                  </KV>
                  <KV k="Jenis unit">
                    {dash(detail.route?.vehicle?.unitType)}
                  </KV>
                  {detail.containerType && (
                    <KV k="Tipe kontainer">{humanize(detail.containerType)}</KV>
                  )}
                </>
              )}
              <KV k="Created">{fmt(detail.createdAt)}</KV>
              {detail.dispatchedAt && (
                <KV k="Dispatched">{fmt(detail.dispatchedAt)}</KV>
              )}
              {detail.cancelledAt && (
                <KV k="Cancelled">{fmt(detail.cancelledAt)}</KV>
              )}
              {detail.cancellationReason && (
                <KV k="Cancellation reason">{detail.cancellationReason}</KV>
              )}
            </Section>

            {/* Catatan tambahan — frozen 4W intake recon notes (BL / FO / AGL
                codes). Keyed by note key; each carries the label ops typed. */}
            {detail.additionalNotes &&
              Object.keys(detail.additionalNotes).length > 0 && (
                <Section title="Catatan tambahan">
                  {Object.entries(detail.additionalNotes).map(([key, note]) => (
                    <KV key={key} k={note.label || humanize(key)}>
                      <span className="font-mono">{dash(note.value)}</span>
                    </KV>
                  ))}
                </Section>
              )}

            {/* Parties */}
            <Section title="Parties">
              <div className="grid grid-cols-2 gap-3">
                {(
                  [
                    {
                      label: 'Sender',
                      name: detail.senderName,
                      phone: detail.senderPhone,
                    },
                    {
                      label: 'Receiver',
                      name: detail.receiverName,
                      phone: detail.receiverPhone,
                    },
                  ] as const
                ).map(({ label, name, phone }) => (
                  <div
                    key={label}
                    className="rounded-lg border border-black-500 p-3.5"
                  >
                    <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-text-soft-400">
                      {label}
                    </div>
                    <div className="mt-2 font-semibold text-text-strong-950">
                      {dash(name)}
                    </div>
                    {phone && (
                      <div className="mt-0.5 font-mono text-xs text-text-sub-600">
                        {phone}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Section>

            {/* Route */}
            {/* DIRECT_4W rides a multi-stop route: list every stop in
                sequence instead of just the shipment's two ends. Falls back
                to origin → destination when the route has no stops yet. */}
            <Section title="Route">
              {detail.type === 'DIRECT_4W' && fourWheelStops.length > 0 ? (
                fourWheelStops.map((stop, i) => {
                  const isLast = i === fourWheelStops.length - 1;
                  return (
                    <RouteNode
                      key={stop.id}
                      kind={
                        i === 0 ? 'origin' : isLast ? 'destination' : 'stop'
                      }
                      label={`Stop ${i + 1}${
                        STOP_TYPE_LABELS[stop.type]
                          ? ` · ${STOP_TYPE_LABELS[stop.type]}`
                          : ''
                      }`}
                      status={stop.status}
                      address={stop.address}
                      showConnector={!isLast}
                    />
                  );
                })
              ) : (
                <>
                  <RouteNode
                    kind="origin"
                    name={detail.senderName}
                    address={detail.originAddress}
                    lat={detail.originLat}
                    long={detail.originLong}
                    phone={detail.originPhone}
                    showConnector
                  />
                  <RouteNode
                    kind="destination"
                    name={detail.receiverName}
                    address={detail.destinationAddress}
                    lat={detail.destinationLat}
                    long={detail.destinationLong}
                    phone={detail.destinationPhone}
                  />
                </>
              )}
            </Section>

            {/* Dokumen Resmi — read-only preview of the ops-attested official
                PDFs. AGL Patimban keeps its direction-specific slots (an empty
                slot reads "Belum diunggah"); every other client sees the
                documents ops named and uploaded, as they are. Upload stays on
                the ops side — the client only views/downloads. */}
            {detail.type === 'DIRECT_4W' && (
              <Section title="Dokumen Resmi">
                <div
                  className="-mt-1 overflow-hidden rounded-lg border border-stroke-soft-200"
                  data-testid="official-documents"
                >
                  {(isPatimbanClient(detail.client?.name ?? clientName)
                    ? (detail.exim === 'IMPORT'
                        ? IMPORT_DOCS
                        : EXPORT_DOCS
                      ).map(
                        ({ type, label }) =>
                          [
                            type,
                            label,
                            detail.officialDocuments?.[type],
                          ] as const
                      )
                    : storedDocuments(detail.officialDocuments).map(
                        ([key, doc]) =>
                          [key, documentLabel(key, doc), doc] as const
                      )
                  ).map(([key, label, doc], i) => (
                    <div
                      key={key}
                      className={`flex items-center gap-3 px-3 py-2.5 ${
                        i > 0 ? 'border-t border-stroke-soft-200' : ''
                      }`}
                    >
                      <i
                        className={`ri-file-pdf-2-line text-[18px] ${
                          doc ? 'text-error-base' : 'text-text-soft-400'
                        }`}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-semibold text-text-strong-950">
                          {label}
                        </p>
                        {doc ? (
                          <p className="truncate text-[11px] text-text-sub-600">
                            {doc.filename} · {fmt(doc.uploadedAt)}
                          </p>
                        ) : (
                          <p className="text-[11px] text-text-soft-400">
                            Belum diunggah
                          </p>
                        )}
                      </div>
                      {doc && (
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary hover:underline"
                        >
                          <i className="ri-external-link-line text-[13px]" />
                          Lihat
                        </a>
                      )}
                    </div>
                  ))}
                  {!isPatimbanClient(detail.client?.name ?? clientName) &&
                    storedDocuments(detail.officialDocuments).length === 0 && (
                      <p className="px-3 py-2.5 text-[12px] text-text-soft-400">
                        Belum ada dokumen.
                      </p>
                    )}
                </div>
              </Section>
            )}

            {/* 4W start/end evidence — DIRECT_4W shipments only; the gateway
                omits `direct4wEvidence` for every other type. Carries the
                "Cetak foto" action that opens the Dokumen Lampiran export. */}
            {detail.direct4wEvidence && (
              <Direct4WEvidenceSection
                evidence={detail.direct4wEvidence}
                onPrint={() => setPrintOpen(true)}
              />
            )}

            {/* Timeline — omitted entirely when the gateway sends nothing */}
            {timeline.length > 0 && (
              <Section title="Timeline">
                <p className="-mt-1.5 mb-3 text-[11px] text-text-soft-400">
                  Terbaru di atas · perubahan status dan bukti dari driver.
                </p>
                <ol>
                  {timeline.map((event, i) => (
                    <TimelineEvent
                      key={event.id}
                      event={event}
                      showConnector={i < timeline.length - 1}
                    />
                  ))}
                </ol>
              </Section>
            )}

            {/* Items */}
            <Section title={`Items (${detail.itemCount ?? items.length})`}>
              {items.length > 0 ? (
                <div className="space-y-2.5">
                  {items.map((it, i) => {
                    const itemName =
                      it.itemName || it.invoice || it.description;
                    const effectiveStatus = it.status || detail.status;
                    const canCancel =
                      !!it.id && effectiveStatus === CANCELLABLE_ITEM_STATUS;
                    const canEdit =
                      !!it.id &&
                      EDITABLE_ITEM_STATUSES.includes(effectiveStatus);
                    return (
                      <div
                        key={it.id || i}
                        className="rounded-lg border border-black-500 p-3.5"
                      >
                        {/* Item name + handling status (derived from the parent
                            shipment when the item carries no own status). Item
                            statuses are their own enum — COMPLETED /
                            RETURNED_TO_HUB / LOST — so they use the package
                            badge, not the shipment one. `condition` sits beside
                            the status: damage no longer halts the flow, so an
                            item can be In Delivery *and* Rusak (§4). */}
                        <div className="flex items-start justify-between gap-3">
                          <span className="break-all text-[13px] font-semibold text-text-strong-950">
                            {dash(itemName)}
                          </span>
                          <span className="flex shrink-0 items-center gap-1.5">
                            <ConditionBadge condition={it.condition} />
                            <PackageStatusBadge status={effectiveStatus} />
                          </span>
                        </div>
                        {/* Qty · koli · weight · volume on a single line, e.g.
                            "Qty 1 · 3 koli · 0.96 kg · 0.006 m³". Koli reads
                            its own field, NOT quantity (koli-trd-v1 §8.4). */}
                        <div className="mt-1.5 text-[13px] text-text-sub-600">
                          Qty{' '}
                          <b className="font-semibold text-text-strong-950">
                            {it.quantity ?? '—'}
                          </b>
                          {' · '}
                          <b className="font-semibold text-text-strong-950">
                            {it.koli ?? 1}
                          </b>{' '}
                          koli
                          {it.weight != null && (
                            <>
                              {' · '}
                              <span className="font-mono">{it.weight} kg</span>
                            </>
                          )}
                          {it.volume != null && (
                            <>
                              {' · '}
                              <span className="font-mono">{it.volume} m³</span>
                            </>
                          )}
                        </div>
                        {/* POD photos for this item (POD-per-package) — renders
                            nothing until proof is captured at delivery. */}
                        <ItemPodGallery media={it.media} />
                        <ReturPanel item={it} />
                        <LegList item={it} />
                        {(canEdit || canCancel) && (
                          <div className="mt-2.5 flex justify-end gap-2">
                            {canEdit && (
                              <button
                                type="button"
                                onClick={() => setEditTarget(it)}
                                className="inline-flex items-center gap-1 rounded-md border border-stroke-sub-300 px-2.5 py-1 text-xs font-medium text-text-sub-600 hover:bg-bg-weak-50"
                              >
                                <i className="ri-edit-line" />
                                Edit
                              </button>
                            )}
                            {canCancel && (
                              <button
                                type="button"
                                onClick={() => setCancelTarget(it)}
                                className="inline-flex items-center gap-1 rounded-md border border-stroke-sub-300 px-2.5 py-1 text-xs font-medium text-error-base hover:bg-error-lighter"
                              >
                                <i className="ri-close-circle-line" />
                                Cancel package
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-text-soft-400">
                  No item detail available
                </p>
              )}
            </Section>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={cancelTarget !== null}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancel}
        title="Cancel this package?"
        message={
          cancelTarget
            ? `"${cancelTarget.itemName || cancelTarget.invoice || 'Paket'}" will be marked cancelled and removed from its shipment totals. This can't be undone.`
            : ''
        }
        confirmLabel="Cancel package"
        cancelLabel="Keep package"
        tone="danger"
        loading={cancelling}
      />

      <ConfirmDialog
        open={cancelShipmentOpen}
        onClose={() => setCancelShipmentOpen(false)}
        onConfirm={handleCancelShipment}
        title="Cancel this shipment?"
        message={
          detail
            ? `Shipment ${detail.waybill} and all of its items will be marked CANCELLED. This can't be undone.`
            : ''
        }
        confirmLabel="Cancel shipment"
        cancelLabel="Keep shipment"
        tone="danger"
        loading={cancellingShipment}
      />

      <EditPackageModal
        open={editTarget !== null}
        pkg={
          editTarget?.id
            ? {
                id: editTarget.id,
                itemName: editTarget.itemName || editTarget.invoice,
                po: editTarget.invoice ?? '',
                weight: editTarget.weight,
                volume: editTarget.volume,
                koli: editTarget.koli,
              }
            : null
        }
        onClose={() => setEditTarget(null)}
        onSaved={() => {
          if (shipmentID) load(shipmentID);
        }}
      />

      {printOpen && detail && (
        <React.Suspense fallback={null}>
          <EvidencePrintModal
            open
            shipment={detail}
            onClose={() => setPrintOpen(false)}
          />
        </React.Suspense>
      )}
    </>,
    document.body
  );
};

export default ShipmentDetailDrawer;
